import { useState, useCallback } from 'react';

import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import Button from '@shared/components/atoms/Button/Button';
import { getApprovalDocList } from '@entities/approval/api/approval';
import {
  getDocumentContentForRecapture,
  updateDocumentContentImage,
} from '@entities/document/api/document';
import {
  captureHtmlContentAsBase64,
  DOCUMENT_CAPTURE_DEFAULTS,
} from '@shared/utils/captureDocumentImage';

import styles from './ReCaptureDocumentImagePage.module.scss';

/**
 * ReCaptureDocumentImagePage
 * - 지정 일자의 문서 목록을 조회한 뒤, 각 문서 본문 HTML을 상신 시와 동일한 방식으로 캡처하여 DB에 저장
 * - 단일 테스트 API(PUT content-image)와 실제 테스트(날짜 지정 → 일괄 재캡처)용
 */
function ReCaptureDocumentImagePage() {
  const [date1, setDate1] = useState('20260225');
  const [date2, setDate2] = useState('20260226');
  const [docList, setDocList] = useState([]);
  const [logLines, setLogLines] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  /** 단일 문서 테스트용 docIdx */
  const [singleDocIdx, setSingleDocIdx] = useState('36781');
  const [isSingleRunning, setIsSingleRunning] = useState(false);
  /** 마지막 일괄 재캡처에서 실패한 docIdx 목록 (실패분만 재시도용) */
  const [failedDocIndices, setFailedDocIndices] = useState([]);
  const [isRetryRunning, setIsRetryRunning] = useState(false);

  const addLog = useCallback(function (message) {
    setLogLines(function (prev) {
      return [...prev, `${new Date().toLocaleTimeString()} ${message}`];
    });
  }, []);

  const handleFetchList = useCallback(
    async function () {
      setIsLoadingList(true);
      setLogLines([]);
      try {
        addLog(`목록 조회: ${date1} ~ ${date2} (type: all)`);
        const response = await getApprovalDocList({
          type: 'all',
          date1,
          date2,
        });
        if (response.result_code !== 0) {
          addLog(`목록 조회 실패: result_code=${response.result_code}`);
          setDocList([]);
          return;
        }
        const list = response.resultData?.docInfoList ?? [];
        const seen = new Set();
        const deduped = list.filter(function (item) {
          if (!item.idx) return true;
          if (seen.has(item.idx)) return false;
          seen.add(item.idx);
          return true;
        });
        setDocList(deduped);
        addLog(`문서 ${deduped.length}건 조회됨`);
      } catch (err) {
        addLog(`목록 조회 오류: ${err.message || String(err)}`);
        setDocList([]);
      } finally {
        setIsLoadingList(false);
      }
    },
    [date1, date2, addLog],
  );

  const runCaptureForList = useCallback(
    async function (list, options) {
      const { logPrefix = '', onFail } = options || {};
      // DOCUMENT_CAPTURE_DEFAULTS(공통 옵션) 사용 — 상신·미리보기와 동일한 결과 보장
      const captureOptions = { ...DOCUMENT_CAPTURE_DEFAULTS };
      let successCount = 0;
      const failed = [];
      for (let i = 0; i < list.length; i += 1) {
        const doc = list[i];
        const docIdx = doc.idx;
        try {
          addLog(`${logPrefix}[${i + 1}/${list.length}] docIdx=${docIdx} 조회 중`);
          const detailRes = await getDocumentContentForRecapture(docIdx);
          if (detailRes.result_code !== 0 || !detailRes.resultData) {
            addLog(`[${docIdx}] 본문 조회 실패`);
            failed.push(docIdx);
            continue;
          }
          const contents =
            detailRes.resultData.contents || detailRes.resultData.contents_html || '';
          if (!contents || !contents.trim()) {
            addLog(`[${docIdx}] 본문 없음, 스킵`);
            continue;
          }
          addLog(`[${docIdx}] 캡처 중`);
          const base64 = await captureHtmlContentAsBase64(contents, captureOptions);
          if (!base64) {
            addLog(`[${docIdx}] 캡처 실패`);
            failed.push(docIdx);
            continue;
          }
          await updateDocumentContentImage(docIdx, base64);
          addLog(`[${docIdx}] 저장 완료`);
          successCount += 1;
        } catch (err) {
          addLog(`[${docIdx}] 오류: ${err.message || String(err)}`);
          failed.push(docIdx);
        }
      }
      if (onFail && failed.length > 0) {
        onFail(failed);
      }
      return { successCount, failed };
    },
    [addLog],
  );

  const handleRunReCapture = useCallback(
    async function () {
      if (docList.length === 0) {
        addLog('먼저 목록을 조회해주세요.');
        return;
      }
      setIsRunning(true);
      setFailedDocIndices([]);
      addLog(`재캡처 시작 (${docList.length}건)`);
      const { successCount, failed } = await runCaptureForList(docList, {
        onFail: setFailedDocIndices,
      });
      addLog(`재캡처 완료: 성공 ${successCount}, 실패 ${failed.length}`);
      setIsRunning(false);
    },
    [docList, addLog, runCaptureForList],
  );

  const handleRetryFailed = useCallback(
    async function () {
      if (failedDocIndices.length === 0) {
        addLog('실패한 문서가 없습니다.');
        return;
      }
      setIsRetryRunning(true);
      addLog(`실패분만 재캡처 시작 (${failedDocIndices.length}건)`);
      const list = failedDocIndices.map(function (idx) {
        return { idx };
      });
      const { successCount, failed } = await runCaptureForList(list, {
        logPrefix: '[재시도] ',
        onFail: setFailedDocIndices,
      });
      addLog(`실패분 재캡처 완료: 성공 ${successCount}, 여전히 실패 ${failed.length}`);
      setIsRetryRunning(false);
    },
    [failedDocIndices, addLog, runCaptureForList],
  );

  const handleSingleReCapture = useCallback(
    async function () {
      const docIdx = Number(singleDocIdx);
      if (!docIdx || Number.isNaN(docIdx)) {
        addLog('단일 테스트: 유효한 문서 번호(docIdx)를 입력하세요.');
        return;
      }
      setIsSingleRunning(true);
      addLog(`[단일] docIdx=${docIdx} 시작`);
      // DOCUMENT_CAPTURE_DEFAULTS(공통 옵션) 사용 — 상신·미리보기와 동일한 결과 보장
      const captureOptions = { ...DOCUMENT_CAPTURE_DEFAULTS };
      try {
        const detailRes = await getDocumentContentForRecapture(docIdx);
        if (detailRes.result_code !== 0 || !detailRes.resultData) {
          addLog(`[${docIdx}] 본문 조회 실패`);
          setIsSingleRunning(false);
          return;
        }
        const contents = detailRes.resultData.contents || detailRes.resultData.contents_html || '';
        if (!contents || !contents.trim()) {
          addLog(`[${docIdx}] 본문 없음`);
          setIsSingleRunning(false);
          return;
        }
        addLog(`[${docIdx}] 캡처 중`);
        const base64 = await captureHtmlContentAsBase64(contents, captureOptions);
        if (!base64) {
          addLog(`[${docIdx}] 캡처 실패`);
          setIsSingleRunning(false);
          return;
        }
        await updateDocumentContentImage(docIdx, base64);
        addLog(`[${docIdx}] 저장 완료`);
      } catch (err) {
        addLog(`[${docIdx}] 오류: ${err.message || String(err)}`);
      } finally {
        setIsSingleRunning(false);
      }
    },
    [singleDocIdx, addLog],
  );

  const tabContent = (
    <>
      <ContentCard title="단일 문서 테스트">
        <div className={styles.formRow}>
          <label className={styles.label}>
            문서 번호 (docIdx)
            <input
              type="text"
              value={singleDocIdx}
              onChange={(e) => setSingleDocIdx(e.target.value)}
              placeholder="36781"
              className={styles.input}
            />
          </label>
          <Button type="button" onClick={handleSingleReCapture} disabled={isSingleRunning}>
            {isSingleRunning ? '변환 중…' : '1건만 재캡처'}
          </Button>
        </div>
        <p className={styles.hint}>해당 문서의 본문 HTML을 캡처한 뒤 DB에 저장합니다.</p>
      </ContentCard>
      <ContentCard title="재캡처 설정">
        <div className={styles.formRow}>
          <label className={styles.label}>
            시작일 (YYYYMMDD)
            <input
              type="text"
              value={date1}
              onChange={(e) => setDate1(e.target.value)}
              placeholder="20260225"
              className={styles.input}
            />
          </label>
          <label className={styles.label}>
            종료일 (YYYYMMDD)
            <input
              type="text"
              value={date2}
              onChange={(e) => setDate2(e.target.value)}
              placeholder="20260226"
              className={styles.input}
            />
          </label>
        </div>
        <div className={styles.buttonRow}>
          <Button type="button" onClick={handleFetchList} disabled={isLoadingList}>
            {isLoadingList ? '조회 중…' : '목록 조회 (실제 테스트 API)'}
          </Button>
          <Button
            type="button"
            onClick={handleRunReCapture}
            disabled={isRunning || docList.length === 0}
          >
            {isRunning ? '재캡처 진행 중…' : '재캡처 실행'}
          </Button>
        </div>
        {docList.length > 0 && (
          <p className={styles.hint}>
            조회된 문서: {docList.length}건 (docIdx: {docList.map((d) => d.idx).join(', ')})
          </p>
        )}
        {failedDocIndices.length > 0 && (
          <div className={styles.failedSection}>
            <p className={styles.hint}>실패한 문서: {failedDocIndices.length}건</p>
            <p className={styles.failedIds}>
              {failedDocIndices.slice(0, 30).join(', ')}
              {failedDocIndices.length > 30 ? ` … 외 ${failedDocIndices.length - 30}건` : ''}
            </p>
            <Button
              type="button"
              onClick={handleRetryFailed}
              disabled={isRetryRunning || isRunning}
            >
              {isRetryRunning ? '재시도 중…' : '실패분만 재캡처'}
            </Button>
          </div>
        )}
      </ContentCard>
      <ContentCard title="실행 로그">
        <div className={styles.logArea}>
          {logLines.length === 0 ? (
            <span className={styles.logPlaceholder}>
              목록 조회 후 재캡처 실행 시 로그가 표시됩니다.
            </span>
          ) : (
            logLines.map((line, idx) => (
              <div key={idx} className={styles.logLine}>
                {line}
              </div>
            ))
          )}
        </div>
      </ContentCard>
    </>
  );

  return (
    <AdminLayout title="문서 본문 이미지 재캡처">
      <TabContentLayout
        tabs={{
          label: '재캡처',
          content: tabContent,
        }}
      />
    </AdminLayout>
  );
}

export default ReCaptureDocumentImagePage;
